import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import IntroCodeSamples from '../../components/intro-code-samples';
export const _frontmatter = {
  "title": "Getting Started"
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Note = makeShortcode("Note");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h1 {...{
      "id": "getting-started"
    }}>{`Getting Started`}</h1>
    <p>{`Install Theme UI.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`npm install theme-ui @emotion/react @mdx-js/react
`}</code></pre>
    <p>{`Create a theme object to include custom color, typography, and layout values.`}</p>
    <IntroCodeSamples.ThemeCreation />
    <p>{`Pass your `}<inlineCode parentName="p">{`theme`}</inlineCode>{` object to `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{`.`}</p>
    <IntroCodeSamples.BasicUsage />
    <Note mdxType="Note">
      <p>{`If you’re using Gatsby, see the
`}<a parentName="p" {...{
          "href": "/getting-started/gatsby"
        }}>{`Getting Started with Gatsby`}</a>{` page.`}</p>
    </Note>
    <h2 {...{
      "id": "sx-prop"
    }}><inlineCode parentName="h2">{`sx`}</inlineCode>{` prop`}</h2>
    <p>{`Use the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop throughout your application to add styles based on your theme
to any component. Enable the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop by adding the
`}<inlineCode parentName="p">{`/** @jsxImportSource theme-ui */`}</inlineCode>{` comment to the top of your file or
configuring `}<inlineCode parentName="p">{`jsxImportSource`}</inlineCode>{` in your Babel config.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop lets you add any valid CSS to an element, while using values from
your theme to keep styles consistent. You can think of the style object that the
`}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop accepts as a `}<em parentName="p">{`superset`}</em>{` of CSS.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`/** @jsxImportSource theme-ui */

export default (props) => (
  <div
    sx={{
      fontWeight: 'bold',
      fontSize: 4, // picks up value from \`theme.fontSizes[4]\`
      color: 'primary', // picks up value from \`theme.colors.primary\`
    }}
  >
    Hello
  </div>
)
`}</code></pre>
    <Note mdxType="Note">
      <p>{`Confused about this `}<inlineCode parentName="p">{`@jsxImportSource`}</inlineCode>{` comment? Read more about
`}<a parentName="p" {...{
          "href": "/guides/jsx-pragma"
        }}>{`JSX Pragma`}</a>{`.`}</p>
    </Note>
    <h3 {...{
      "id": "classic-jsx-runtime"
    }}>{`Classic JSX runtime`}</h3>
    <p>{`If you use the classic JSX runtime, the `}<inlineCode parentName="p">{`/** @jsxImportSource theme-ui */`}</inlineCode>{`
comment has no effect. In this case enable the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop by adding the
`}<inlineCode parentName="p">{`/** @jsx jsx */`}</inlineCode>{` comment instead and importing `}<inlineCode parentName="p">{`jsx`}</inlineCode>{` from Theme UI.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`/** @jsx jsx */
import { jsx } from 'theme-ui'
`}</code></pre>
    <h2 {...{
      "id": "responsive-styles"
    }}>{`Responsive styles`}</h2>
    <p>{`The `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop also supports using arrays as values to change properties
responsively with a mobile-first approach.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`/** @jsxImportSource theme-ui */

export default (props) => (
  <div
    sx={{
      // applies width 100% to all viewport widths,
      // width 50% above the first breakpoint,
      // and 25% above the next breakpoint
      width: ['100%', '50%', '25%'],
    }}
  />
)
`}</code></pre>
    <Note mdxType="Note">
      <p>{`This API originated in `}<a parentName="p" {...{
          "href": "https://styled-system.com"
        }}>{`Styled System`}</a>{` and is intended as
`}<a parentName="p" {...{
          "href": "https://styled-system.com/guides/array-props"
        }}>{`a terser syntax for applying responsive styles`}</a>{`
across a singular dimension. The values used for media queries can be defined in
a `}<a parentName="p" {...{
          "href": "/theming/#breakpoints"
        }}><inlineCode parentName="a">{`theme.breakpoints`}</inlineCode></a>{` array.`}</p>
    </Note>
    <p>{`If you prefer standard CSS syntax, you can use nested objects for responsive
styles as well.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<div
  sx={{
    width: '100%',
    '@media screen and (min-width: 40em)': {
      width: '50%',
    },
  }}
/>
`}</code></pre>
    <h2 {...{
      "id": "components"
    }}>{`Components`}</h2>
    <p>{`Theme UI includes a library of primitive UI components, which can be completely
customized with your theme and include support for
`}<a parentName="p" {...{
        "href": "/components/variants"
      }}>{`variants`}</a>{`. You can use variants to encapsulate more
complex, component-specific styles that can be changed with props. Import and
use these components to handle layout, images, forms, and more.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`/** @jsxImportSource theme-ui */
import { Box, Label, Input, Button } from 'theme-ui'

export default (props) => (
  <Box sx={{ mb: 4 }}>
    <Label htmlFor="search">Search</Label>
    <Input id="search" name="search" {...props} />
    <Button>Go</Button>
  </Box>
)
`}</code></pre>
    <p>{`See the `}<a parentName="p" {...{
        "href": "/components"
      }}>{`Components`}</a>{` documentation for more.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      